
import React, { Suspense } from 'react';
import { useLayout } from '@/contexts/LayoutContext';
import { SidebarProvider, SidebarContent, SidebarFooter } from "@/components/ui/sidebar";
import SidebarHeader from '@/components/sidebar/SidebarHeader';
import CursoSidebar from '@/components/sidebar/CursoSidebar';
import WorkspaceSidebar from '@/components/sidebar/WorkspaceSidebar';
import UserProfileSection from '@/components/sidebar/UserProfileSection';
import OverdueTasksDialog from '@/components/OverdueTasksDialog';
import Footer from '@/components/Footer';

// Simple error boundary for catching layout errors
class LayoutErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Layout error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-600 text-xl font-semibold mb-4">Layout Error</h2>
            <p className="mb-4">There was a problem loading the application layout.</p>
            <pre className="bg-white p-4 rounded border border-red-100 text-sm overflow-auto mb-4">
              {this.state.error?.message}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { isSidebarOpen, sidebarMode, currentStage } = useLayout();
  
  console.log("AppLayout rendering, sidebar mode:", sidebarMode);
  
  return (
    <LayoutErrorBoundary>
      <div className="min-h-screen flex w-full overflow-hidden">
        <SidebarProvider defaultOpen={isSidebarOpen}>
          <div className="sidebar-container fixed h-full" style={{ width: '320px', zIndex: 40 }}>
            <div className="h-full flex flex-col">
              <SidebarHeader />
              
              {sidebarMode === 'curso' ? (
                <SidebarContent>
                  <Suspense fallback={<div className="p-4">Loading sidebar...</div>}>
                    <CursoSidebar />
                  </Suspense>
                </SidebarContent>
              ) : (
                <SidebarContent>
                  <Suspense fallback={<div className="p-4">Loading sidebar...</div>}>
                    <WorkspaceSidebar />
                  </Suspense>
                </SidebarContent>
              )}
              
              <SidebarFooter>
                <Suspense fallback={<div className="p-4">Loading profile...</div>}>
                  <UserProfileSection className="mt-auto" />
                </Suspense>
              </SidebarFooter>
            </div>
          </div>
          
          <div className="flex flex-col min-h-screen flex-1 bg-[#FFFFFF] ml-[320px]" style={{ width: 'calc(100% - 320px)' }}>
            <Suspense fallback={<LoadingFallback />}>
              <OverdueTasksDialog />
              <div className="flex flex-col min-h-[calc(100vh-64px)] bg-[#FFFFFF] sidebar-full-height">
                <div className="flex-grow max-w-full overflow-x-hidden">
                  {children}
                </div>
                <Footer />
              </div>
            </Suspense>
          </div>
        </SidebarProvider>
      </div>
    </LayoutErrorBoundary>
  );
};

export default AppLayout;
